/*
  Keeps the product page in sync with the currently selected purchase option.

  Whatever the customer picks (a quantity tier or a subscription plan) has to end
  up in the product form, in the sticky cart form and in both price blocks, so the
  sticky cart always shows the same as the widget. A subscription also puts its
  delivery frequency there. The cart drawer is not touched, it renders whatever
  was added.

  Used by <purchase-options>.
*/
if (!window.PdpSelectionSync) {
  class PdpSelectionSync {
    /*
      formSelector: selector matching every <form> that has to carry the selection
      priceSelector: selector matching every price block that has to show the selection
      subscribeLabel: submit button label used while a subscription is selected
      removeLabel: submit button label used while that subscription is already in the cart

      removeMode is set from the outside whenever the cart changes: it turns the
      subscribe button into the button that takes the subscription out again.
    */
    constructor({ formSelector, priceSelector, subscribeLabel, removeLabel } = {}) {
      this.formSelector = formSelector;
      this.priceSelector = priceSelector;
      this.subscribeLabel = subscribeLabel;
      this.removeLabel = removeLabel;
      this.removeMode = false;
    }

    update(selection) {
      if (this.formSelector) {
        document.querySelectorAll(this.formSelector).forEach((form) => {
          this.updateQuantity(form, selection.quantity);
          this.updateSellingPlan(form, selection.sellingPlanId);
          this.updateSubmitButton(form, selection.sellingPlanId);
        });
      }

      if (this.priceSelector) {
        document.querySelectorAll(this.priceSelector).forEach((container) => {
          this.updatePrice(container, selection);
        });
      }

      this.updateStickySubscription(selection.label, selection.sellingPlanId);
    }

    /* Badge and delivery frequency of the sticky cart, a one-time purchase has neither. */
    updateStickySubscription(label, sellingPlanId) {
      const target = document.querySelector('[data-sticky-subscription]');
      if (!target) return;

      const frequency = target.querySelector('[data-subscription-frequency]');
      if (frequency) {
        frequency.textContent = sellingPlanId ? label || '' : '';
      }
      target.hidden = !sellingPlanId;
    }

    updateQuantity(form, quantity) {
      if (!quantity) return;
      this.fieldsNamed(form, 'quantity').forEach((input) => {
        input.value = quantity;
      });
    }

    updateSellingPlan(form, sellingPlanId) {
      let input = this.fieldsNamed(form, 'selling_plan')[0];

      if (!input) {
        if (!sellingPlanId) return;
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'selling_plan';
        form.appendChild(input);
      }

      input.value = sellingPlanId || '';
      // Disabled fields are left out of the FormData sent to /cart/add.
      input.disabled = !sellingPlanId;
      form.classList.toggle('product-form--subscription', Boolean(sellingPlanId));
    }

    updateSubmitButton(form, sellingPlanId) {
      const button = form.querySelector('[type="submit"]');
      if (!button || !this.subscribeLabel) return;

      // A sold out or unavailable variant keeps the label the theme has set.
      if (button.hasAttribute('disabled')) return;

      const label = button.textContent.trim();
      if (!button.dataset.defaultLabel && label !== this.subscribeLabel && label !== this.removeLabel) {
        button.dataset.defaultLabel = label;
      }

      const removes = Boolean(sellingPlanId) && this.removeMode && Boolean(this.removeLabel);

      if (sellingPlanId) {
        button.textContent = removes ? this.removeLabel : this.subscribeLabel;
      } else {
        button.textContent = button.dataset.defaultLabel || label;
      }

      // The flag is what turns the click into a removal instead of a submit.
      button.classList.toggle('product-form__submit--remove', removes);
      if (removes) {
        button.dataset.removeSubscription = 'true';
      } else {
        delete button.dataset.removeSubscription;
      }
    }

    /*
      A discounted selection is rendered with the same markup and the same classes as the
      one <quantity-discount-picker> builds, so the price block looks identical on every
      product: sale price first, compare price next to it, discounted unit price below.
    */
    updatePrice(container, { priceMoney, compareMoney, unitMoney }) {
      const price = container.matches('.price') ? container : container.querySelector('.price');
      if (!price || !priceMoney) return;

      // The discount markup below replaces the theme's own sale state.
      price.classList.remove('price--on-sale');

      const regular = price.querySelector('.price__regular:not(.quantity-discount)');
      const regularItem = regular ? regular.querySelector('.price-item--regular') : null;
      if (regularItem) {
        regularItem.innerHTML = this.money(compareMoney || priceMoney);
      }
      if (regular) {
        regular.classList.toggle('hidden', Boolean(compareMoney));
      }

      const discount = this.discountPrice(price, regular, compareMoney);
      if (discount) {
        discount.querySelector('.price-item--regular').innerHTML = this.money(compareMoney);
        discount.querySelector('.price-item--sale').innerHTML = this.money(priceMoney);
        discount.classList.toggle('hidden', !compareMoney);
      }

      this.updateUnitPrice(price, unitMoney, compareMoney);
    }

    updateUnitPrice(price, unitMoney, compareMoney) {
      const regular = price.querySelector('.unit-price[data-price]');
      if (!regular || !unitMoney) return;

      const regularItem = regular.querySelector('[data-unit-price]');
      if (regularItem && !compareMoney) {
        regularItem.innerHTML = this.money(unitMoney);
      }
      regular.classList.toggle('hidden', Boolean(compareMoney));

      const discount = this.discountUnitPrice(price, regular, compareMoney);
      if (discount) {
        discount.querySelector('[data-unit-price]').innerHTML = this.money(unitMoney);
        discount.classList.toggle('hidden', !compareMoney);
      }
    }

    discountPrice(price, regular, compareMoney) {
      let element = price.querySelector('.price__sale.quantity-discount');
      if (element || !regular || !compareMoney) return element;

      const labels = this.priceLabels(price);
      regular.insertAdjacentHTML(
        'afterend',
        `<div class="price__sale quantity-discount">
          <dt class="price__compare">
            <span class="visually-hidden visually-hidden--inline">${labels.regular}</span>
          </dt>
          <dd class="price__compare">
            <s class="price-item price-item--regular"></s>
          </dd>
          <dt>
            <span class="visually-hidden visually-hidden--inline">${labels.sale}</span>
          </dt>
          <dd>
            <span class="price-item price-item--sale"></span>
          </dd>
        </div>`
      );

      return price.querySelector('.price__sale.quantity-discount');
    }

    discountUnitPrice(price, regular, compareMoney) {
      let element = price.querySelector('.unit-price.quantity-discount');
      if (element || !compareMoney) return element;

      const base = regular.querySelector('[data-unit-base]');
      const separator = regular.querySelector('.visually-hidden');
      regular.insertAdjacentHTML(
        'afterend',
        `<dd class="unit-price quantity-discount">
          <span data-unit-price></span>
          <span aria-hidden="true">/</span>
          <span class="visually-hidden">${separator ? separator.innerHTML : ''}</span>
          <span data-unit-base>${base ? base.innerHTML : ''}</span>
        </dd>`
      );

      return price.querySelector('.unit-price.quantity-discount');
    }

    /* The visually hidden labels of the theme price block, so they stay translated. */
    priceLabels(price) {
      const regular = price.querySelector('.price__compare .visually-hidden');
      const sale = price.querySelector('.price__sale:not(.quantity-discount)');
      const saleLabel = sale && sale.closest('dd') ? sale.closest('dd').previousElementSibling : null;

      return {
        regular: regular ? regular.textContent.trim() : '',
        sale: saleLabel ? saleLabel.textContent.trim() : ''
      };
    }

    money(value) {
      return `<price-money><bdi>${value}</bdi></price-money>`;
    }

    /* form.elements returns a single node or a collection, depending on the name. */
    fieldsNamed(form, name) {
      const field = form.elements[name];
      if (!field) return [];
      return field.nodeType ? [field] : Array.from(field);
    }
  }

  window.PdpSelectionSync = PdpSelectionSync;
}
